/* ==========================================================================
   BL101 Full Hero — slideshow
   --------------------------------------------------------------------------
   When the Full Hero section has Slide blocks, its slides are stacked in one
   grid cell (see section-full-hero.css) and this element crossfades between
   them on a timer set by the section's "Time per slide" setting.

     - Pauses while anything inside has keyboard focus, and stays paused once
       the visitor presses the pause button.
     - Does not auto-advance when the visitor prefers reduced motion; the
       dots still work and the play button starts it.
     - Inactive slides are aria-hidden + inert so their links can't be
       tabbed to while invisible.
     - Swipe (touch/pen) or click-and-drag (mouse) left/right for the
       next/previous slide. The
       slides only allow vertical panning and pinch-zoom (touch-action in
       the CSS), so page scrolling still works and horizontal drags reach us.
     - In the theme editor, selecting a Slide block jumps to it and holds.

   With JS off the first slide simply shows on its own.
   ========================================================================== */

class FullHeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.pauseButton = this.querySelector('[data-pause]');
    if (this.slides.length < 2) return;

    this.index = 0;
    this.interval = Number(this.dataset.interval) || 5000;
    this.timer = null;
    this.focused = false;
    this.editorHold = false;
    // Paused by the visitor (pause button) or, initially, by reduced motion.
    this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.goTo(Number(dot.dataset.dot));
        this.restart();
      });
    });

    this.pauseButton?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.restart();
    });

    // Keyboard focus only, so clicking/tapping a dot doesn't leave it paused.
    this.addEventListener('focusin', (event) => {
      if (!(/** @type {Element} */ (event.target).matches(':focus-visible'))) return;
      this.focused = true;
      this.restart();
    });
    this.addEventListener('focusout', (event) => {
      if (this.contains(/** @type {Node} */ (event.relatedTarget))) return;
      this.focused = false;
      this.restart();
    });

    this.setupSwipe();

    // Theme editor: show the selected slide and stop rotating until deselected.
    this.onBlockSelect = (event) => {
      const i = this.slides.indexOf(event.target);
      if (i === -1) return;
      this.editorHold = true;
      this.goTo(i);
      this.restart();
    };
    this.onBlockDeselect = (event) => {
      if (!this.slides.includes(event.target)) return;
      this.editorHold = false;
      this.restart();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.restart();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  /**
   * Changes slide on a horizontal swipe or mouse drag. A swipe/drag that
   * ends on a slide link doesn't also follow the link.
   */
  setupSwipe() {
    const area = this.querySelector('.full-hero__slides');
    if (!area) return;

    // Minimum horizontal travel, in px, for a gesture to count as a swipe.
    const THRESHOLD = 40;
    let start = null;
    let swiped = false;

    area.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      start = { x: event.clientX, y: event.clientY };
      swiped = false;
    });

    // Mouse drags would otherwise pick up the image/link as a native drag
    // (the "ghost image"), which swallows the pointerup.
    area.addEventListener('dragstart', (event) => event.preventDefault());

    area.addEventListener('pointerup', (event) => {
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      start = null;
      if (Math.abs(dx) < THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;

      swiped = true;
      const step = dx < 0 ? 1 : -1;
      this.goTo((this.index + step + this.slides.length) % this.slides.length);
      this.restart();
    });

    // The browser took over the gesture (e.g. a vertical page scroll).
    area.addEventListener('pointercancel', () => {
      start = null;
    });

    area.addEventListener(
      'click',
      (event) => {
        if (!swiped) return;
        swiped = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  /** Whether the timer should currently be running. */
  get isPlaying() {
    return !this.userPaused && !this.focused && !this.editorHold;
  }

  /** Clears any pending advance and schedules the next one if playing. */
  restart() {
    clearTimeout(this.timer);
    this.toggleAttribute('data-paused', this.userPaused);
    if (this.pauseButton) {
      this.pauseButton.setAttribute(
        'aria-label',
        this.userPaused ? 'Play slideshow' : 'Pause slideshow'
      );
    }
    if (!this.isPlaying) return;
    this.timer = setTimeout(() => {
      this.goTo((this.index + 1) % this.slides.length);
      this.restart();
    }, this.interval);
  }

  /** @param {number} next */
  goTo(next) {
    if (next === this.index) return;
    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      if (active) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });
    this.dots.forEach((dot, i) => {
      if (i === next) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
    this.index = next;
  }
}

if (!customElements.get('full-hero-slideshow')) {
  customElements.define('full-hero-slideshow', FullHeroSlideshow);
}
